import React from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import compose from "recompose/compose";
import branch from "recompose/branch";
import withIsRequisitionListEnabled from "../withIsRequisitionListEnabled";
import withAllOptionsSet from "./withAllOptionsSet";
import withEnsureLoggedIn from "theme/modules/User/helpers/withEnsureLoggedIn";
import withLoadRequisitionLists from "./withLoadRequisitionLists";
import withAddMultipleItemsToRequisitionListMutation from "./withAddMultipleItemsToRequisitionListMutation";
import withAddMultipleItemsToNewRequisitionListMutation from "./withAddMultipleItemsToNewRequisitionListMutation";
import withProductConfigurationModal from "./withProductConfigurationModal";

const productOptionsPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.string.isRequired,
    values: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      })
    ).isRequired,
    attribute: PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
  })
);
export const productPropTypes = PropTypes.shape({
  sku: PropTypes.string.isRequired,
  options: productOptionsPropTypes,
  custom_options: productOptionsPropTypes,
  bundleOptions: productOptionsPropTypes,
});
export const selectedConfigurableOptionsPropTypes = PropTypes.oneOfType([
  PropTypes.objectOf(PropTypes.string.isRequired),
  PropTypes.arrayOf(
    PropTypes.shape({
      optionId: PropTypes.string.isRequired,
      optionValue: PropTypes.string.isRequired,
    })
  ),
  PropTypes.arrayOf(
    PropTypes.shape({
      optionId: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ),
  PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ),
  PropTypes.arrayOf(
    PropTypes.shape({
      optionId: PropTypes.string.isRequired,
      optionValue: PropTypes.string.isRequired,
    })
  ),
]);

const propTypes = {
  id: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      product: productPropTypes.isRequired,
      quantity: PropTypes.number,
      selectedConfigurableOptions:
        selectedConfigurableOptionsPropTypes.isRequired,
      selectedCustomOptions: selectedConfigurableOptionsPropTypes.isRequired,
      selectedBundleOptions: selectedConfigurableOptionsPropTypes.isRequired,
    }).isRequired
  ).isRequired,
  size: PropTypes.string,
  showOptionsModalIfNotFullyConfigured: PropTypes.bool,
};

/**
 * @typedef {import('../withIsRequisitionListEnabled').OutputProps & import('./withAllOptionsSet').OutputProps & import('./withLoadRequisitionLists').OutputProps & import('./withAddMultipleItemsToRequisitionListMutation').OutputProps & import('./withAddMultipleItemsToNewRequisitionListMutation').OutputProps & import('./withProductConfigurationModal').OutputProps} OutputProps
 */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} InputProps
 */

/**
 * @typedef {React.ComponentType<InputProps & OutputProps>} BaseComponent
 */

/**
 * @typedef {React.ComponentType<InputProps>} EnhancedComponent
 */

/**
 * @callback AddToRequisitionListEnhancer
 * @param {BaseComponent} BaseComponent
 * @returns {EnhancedComponent}
 */

/**
 * @returns {AddToRequisitionListEnhancer}
 */
export default () => (BaseComponent) => {
  const EnhancedComponent = compose(
    withIsRequisitionListEnabled(),
    branch(
      ({ requisitionList }) => requisitionList.isFeatureActive,
      (BaseComponent) => BaseComponent,
      () => () => null
    ),
    withEnsureLoggedIn({
      message: (
        <FormattedMessage
          id="modules.RequisitionList.AddToRequisitionList.needLogin"
          defaultMessage="You must log in before adding to a requisition list."
        />
      ),
    }),
    withAllOptionsSet,
    withLoadRequisitionLists,
    withAddMultipleItemsToRequisitionListMutation,
    withAddMultipleItemsToNewRequisitionListMutation,
    withProductConfigurationModal
  )(BaseComponent);

  EnhancedComponent.propTypes = propTypes;

  return EnhancedComponent;
};
